import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { Home, ChevronRight, Phone, Mail, MapPin, Clock, Send, Building2, CheckCircle, User, MessageSquare, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { CTASection } from '../components/CTASection';
interface ContactPageProps {
  onNavigate?: (page: string, tab?: string) => void;
}
interface FormData {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  serviceType: string;
  message: string;
}
export function ContactPage({
  onNavigate
}: ContactPageProps) {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    serviceType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const serviceTypes = ['Vehicle Shipping', 'Container Shipping', 'Customs Clearance', 'Freight Forwarding', 'Documentation Services', 'Other'];
  const contactInfo = [{
    icon: Phone,
    label: 'Phone',
    value: '+1 301-485-8510',
    href: 'tel:+13014858510'
  }, {
    icon: Mail,
    label: 'Email',
    value: 'info@taskrunner.com',
    href: 'mailto:info@taskrunner.com'
  }, {
    icon: MapPin,
    label: 'Address',
    value: 'Laurel, Maryland, United States',
    href: 'https://maps.google.com/?q=Laurel,Maryland'
  }];
  const businessHours = [{
    day: 'Monday - Friday',
    hours: '8:00 AM - 6:00 PM EST'
  }, {
    day: 'Saturday',
    hours: '9:00 AM - 2:00 PM EST'
  }, {
    day: 'Sunday',
    hours: 'Closed'
  }];
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setIsSubmitted(true);
  };
  if (isSubmitted) {
    return <>
        <section className="relative bg-gray-100 pt-24 lg:pt-32 pb-20" data-id="element-1678">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1679">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="max-w-lg mx-auto text-center py-20" data-id="element-1680">
              <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1681">
                <CheckCircle className="w-10 h-10 text-success" data-id="element-1682" />
              </div>
              <h2 className="font-serif text-3xl font-medium text-gray-900 mb-4" data-id="element-1683">
                Message Sent Successfully!
              </h2>
              <p className="text-gray-600 mb-8" data-id="element-1684">
                Thank you for reaching out. Our team will get back to you within
                24 hours.
              </p>
              <Button variant="secondary" onClick={() => onNavigate?.('home')} data-id="element-1685">
                Back to Home
              </Button>
            </motion.div>
          </div>
        </section>
      </>;
  }
  return <>
      {/* Hero Section */}
      <section className="relative bg-gray-100 pt-24 lg:pt-32 pb-16" data-id="element-1686">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1687">
          {/* Breadcrumb */}
          <motion.nav initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mb-8" data-id="element-1688">
            <ol className="flex items-center gap-2 text-sm" data-id="element-1689">
              <li data-id="element-1690">
                <button onClick={() => onNavigate?.('home')} className="flex items-center gap-1 text-gray-500 hover:text-brand transition-colors" data-id="element-1691">
                  <Home className="w-4 h-4" data-id="element-1692" />
                  <span data-id="element-1693">Home</span>
                </button>
              </li>
              <li data-id="element-1694">
                <ChevronRight className="w-4 h-4 text-gray-400" data-id="element-1695" />
              </li>
              <li data-id="element-1696">
                <span className="text-brand font-medium" data-id="element-1697">Contact</span>
              </li>
            </ol>
          </motion.nav>

          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1698">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-block px-4 py-1.5 bg-brand/10 text-brand text-sm font-semibold rounded-full mb-6" data-id="element-1699">
              Get In Touch
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="font-serif text-4xl sm:text-5xl font-medium text-gray-900 mb-6" data-id="element-1700">
              Let's Start a{' '}
              <span className="text-secondary italic" data-id="element-1701">Conversation</span>
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-lg text-gray-600" data-id="element-1702">
              Have questions about our services? Need a quote? Our team is ready
              to help you with all your shipping needs.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white" data-id="element-1703">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1704">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16" data-id="element-1705">
            {/* Contact Form - 3 columns */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} className="lg:col-span-3" data-id="element-1706">
              <div className="bg-white rounded-3xl border border-gray-200 p-8 lg:p-10 shadow-sm" data-id="element-1707">
                <h2 className="font-serif text-2xl font-medium text-gray-900 mb-2" data-id="element-1708">
                  Send Us a Message
                </h2>
                <p className="text-gray-600 mb-8" data-id="element-1709">
                  Fill out the form below and we'll get back to you shortly.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1710">
                  {/* Name & Email Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1711">
                    <div data-id="element-1712">
                      <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1713">
                        Full Name <span className="text-secondary" data-id="element-1714">*</span>
                      </label>
                      <div className="relative" data-id="element-1715">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1716" />
                        <input type="text" id="fullName" name="fullName" required value={formData.fullName} onChange={handleInputChange} className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand transition-colors" placeholder="John Doe" data-id="element-1717" />
                      </div>
                    </div>
                    <div data-id="element-1718">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1719">
                        Email Address <span className="text-secondary" data-id="element-1720">*</span>
                      </label>
                      <div className="relative" data-id="element-1721">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1722" />
                        <input type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange} className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand transition-colors" placeholder="john@example.com" data-id="element-1723" />
                      </div>
                    </div>
                  </div>

                  {/* Phone & Company Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1724">
                    <div data-id="element-1725">
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1726">
                        Phone Number
                      </label>
                      <div className="relative" data-id="element-1727">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1728" />
                        <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand transition-colors" placeholder="+1 (555) 000-0000" data-id="element-1729" />
                      </div>
                    </div>
                    <div data-id="element-1730">
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1731">
                        Company{' '}
                        <span className="text-gray-400 font-normal" data-id="element-1732">
                          (Optional)
                        </span>
                      </label>
                      <div className="relative" data-id="element-1733">
                        <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1734" />
                        <input type="text" id="company" name="company" value={formData.company} onChange={handleInputChange} className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand transition-colors" placeholder="Your Company" data-id="element-1735" />
                      </div>
                    </div>
                  </div>

                  {/* Service Type */}
                  <div data-id="element-1736">
                    <label htmlFor="serviceType" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1737">
                      Service Type <span className="text-secondary" data-id="element-1738">*</span>
                    </label>
                    <div className="relative" data-id="element-1739">
                      <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1740" />
                      <select id="serviceType" name="serviceType" required value={formData.serviceType} onChange={handleInputChange} className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand transition-colors appearance-none bg-white" data-id="element-1741">
                        <option value="" data-id="element-1742">Select a service</option>
                        {serviceTypes.map(type => <option key={type} value={type} data-id="element-1743">
                            {type}
                          </option>)}
                      </select>
                      <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 rotate-90" data-id="element-1744" />
                    </div>
                  </div>

                  {/* Message */}
                  <div data-id="element-1745">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1746">
                      Message <span className="text-secondary" data-id="element-1747">*</span>
                    </label>
                    <div className="relative" data-id="element-1748">
                      <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-gray-400" data-id="element-1749" />
                      <textarea id="message" name="message" required rows={5} value={formData.message} onChange={handleInputChange} className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand transition-colors resize-none" placeholder="Tell us about your shipping needs..." data-id="element-1750" />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button type="submit" variant="secondary" size="lg" fullWidth disabled={isSubmitting} className="mt-4" data-id="element-1751">
                    {isSubmitting ? <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1752">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1753"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1754"></path>
                        </svg>
                        Sending...
                      </> : <>
                        Send Message
                        <Send className="w-5 h-5 ml-2" data-id="element-1755" />
                      </>}
                  </Button>
                </form>
              </div>

              {/* Need Immediate Assistance - Now below the form */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5
            }} className="mt-6 bg-secondary/10 rounded-2xl p-6 border border-secondary/20" data-id="element-1756">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-1757">
                  Need Immediate Assistance?
                </h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-1758">
                  Our support team is available 24/7 for urgent shipping
                  inquiries.
                </p>
                <a href="tel:+13014858510" className="inline-flex items-center gap-2 text-secondary font-semibold hover:underline" data-id="element-1759">
                  <Phone className="w-4 h-4" data-id="element-1760" />
                  Call Now: +1 301-485-8510
                </a>
              </motion.div>
            </motion.div>

            {/* Contact Info & Map - 2 columns */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="lg:col-span-2 space-y-8" data-id="element-1761">
              {/* Contact Info Cards */}
              <div className="space-y-4" data-id="element-1762">
                {contactInfo.map((info, index) => {
                const Icon = info.icon;
                return <motion.a key={info.label} href={info.href} target={info.label === 'Address' ? '_blank' : undefined} rel={info.label === 'Address' ? 'noopener noreferrer' : undefined} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.3 + index * 0.1
                }} className="flex items-start gap-4 p-5 bg-gray-50 rounded-2xl border border-gray-100 hover:border-brand/30 hover:bg-brand/5 transition-all group" data-id="element-1763">
                      <div className="w-12 h-12 bg-brand/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-brand group-hover:text-white transition-colors" data-id="element-1764">
                        <Icon className="w-5 h-5 text-brand group-hover:text-white transition-colors" data-id="element-1765" />
                      </div>
                      <div data-id="element-1766">
                        <div className="text-sm text-gray-500 mb-1" data-id="element-1767">
                          {info.label}
                        </div>
                        <div className="font-semibold text-gray-900 group-hover:text-brand transition-colors" data-id="element-1768">
                          {info.value}
                        </div>
                      </div>
                    </motion.a>;
              })}
              </div>

              {/* Business Hours */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.6
            }} className="bg-brand rounded-2xl p-6 text-white" data-id="element-1769">
                <div className="flex items-center gap-3 mb-4" data-id="element-1770">
                  <Clock className="w-6 h-6 text-white" data-id="element-1771" />
                  <h3 className="font-bold text-lg text-white" data-id="element-1772">
                    Business Hours
                  </h3>
                </div>
                <div className="space-y-3" data-id="element-1773">
                  {businessHours.map((item, index) => <div key={index} className="flex justify-between items-center text-sm" data-id="element-1774">
                      <span className="text-white/80" data-id="element-1775">{item.day}</span>
                      <span className="font-medium text-white" data-id="element-1776">
                        {item.hours}
                      </span>
                    </div>)}
                </div>
              </motion.div>

              {/* Map */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.7
            }} className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm" data-id="element-1777">
                <div className="aspect-[4/3] bg-gray-100 relative" data-id="element-1778">
                  <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d49547.36242091!2d-76.88!3d39.1!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89b7e0e0e0e0e0e1%3A0x0!2sLaurel%2C%20MD!5e0!3m2!1sen!2sus!4v1234567890" width="100%" height="100%" style={{
                  border: 0
                }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Office Location" className="absolute inset-0" data-id="element-1779" />
                </div>
                <div className="p-4 bg-white" data-id="element-1780">
                  <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-1781">
                    <MapPin className="w-4 h-4 text-brand" data-id="element-1782" />
                    <span data-id="element-1783">Laurel, Maryland, United States</span>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <CTASection onNavigate={onNavigate} data-id="element-1784" />
    </>;
}